import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GridColDef, GridCellParams, GridSortModel } from '@mui/x-data-grid';
import Typography from '@mui/material/Typography';
import { DataGrid, DataGridModel } from 'src/shared/components/organisms/dataGrid';
import { PAGE_SIZE_OPTIONS } from 'src/shared/models/enums/gridFilterConfig';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import { HelpPageSearchFilter } from '../models/helpPageSearchFilter';
import { HelpPageListItem } from '../models/helpPageListItem';
import { HelpPageCategory } from '../models/helpPageCategory';
import { HelpPageModule } from '../models/helpPageModule';
import { getAllHelpPages } from '../services/helpPageService';
import { getAuthorityLabel } from '../functions/getAuthorityLabel';
import { HelpPageStatusChip } from './helpPageStatusChip';

type HelpArticlesDataGridProps = {
  searchFilter: HelpPageSearchFilter;
  searchFilterCallback: (searchFilter: HelpPageSearchFilter) => void;
  categories: HelpPageCategory[];
  modules: HelpPageModule[];
  refetchDataGrid: boolean;
  setRefetchDataGrid: (refetch: boolean) => void;
  onSelectHelpPage: (helpPage: HelpPageListItem) => void;
  /** Döljer kategori-kolumnen — används när griden redan är avgränsad till en enda kategori. */
  hideCategoryColumn?: boolean;
  /** Visar artikelns plats i kategorin. Bara meningsfull när griden visar en enda kategori, eftersom platsen bara gäller inom den. */
  showSortOrderColumn?: boolean;
};

export const HelpArticlesDataGrid = (props: HelpArticlesDataGridProps) => {
  const {
    searchFilter,
    searchFilterCallback,
    categories,
    modules,
    refetchDataGrid,
    setRefetchDataGrid,
    onSelectHelpPage,
    hideCategoryColumn,
    showSortOrderColumn,
  } = props;
  const { t } = useTranslation(['helpArticles']);
  const isMounted = useIsMounted();

  const [rows, setRows] = useState<HelpPageListItem[]>([]);
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: searchFilter.sortCol, sort: searchFilter.sortDir }]);
  const [data, setData] = useState<DataGridModel>({
    loading: true,
    totalRows: 0,
    rowsPerPageOptions: PAGE_SIZE_OPTIONS,
    pageSize: searchFilter.pageSize,
    page: searchFilter.pageIndex,
  });
  const updateData = (k, v) => setData((prev) => ({ ...prev, [k]: v }));
  const latestRequest = useRef(0);

  // En arkiverad sida kan ligga i en kategori som raderats. Tomt medan kategorierna laddas.
  const categoryName = (categoryId: number) =>
    categories.find((c) => c.id === categoryId)?.name ?? (categories.length > 0 ? t('helpArticles:form.deletedCategory') : '');
  const moduleNames = (moduleIds: number[]) =>
    moduleIds
      .map((id) => modules.find((m) => m.id === id)?.name)
      .filter(Boolean)
      .join(', ');

  useEffect(() => {
    setData((prev) => ({ ...prev, page: searchFilter.pageIndex, pageSize: searchFilter.pageSize }));
  }, [searchFilter]);

  const onPageChange = (newPageIndex: number) => {
    searchFilterCallback({ ...searchFilter, pageIndex: newPageIndex });
  };

  const onPageSizeChange = (newPageSize: number) => {
    searchFilterCallback({ ...searchFilter, pageIndex: 0, pageSize: newPageSize });
  };

  const fetchData = useCallback(async () => {
    // Bara svaret på den senaste hämtningen visas, så att ett långsamt svar på ett äldre filter inte skriver över ett nyare.
    latestRequest.current += 1;
    const request = latestRequest.current;

    if (isMounted()) {
      updateData('loading', true);
    }

    try {
      const result = await getAllHelpPages(searchFilter);
      if (isMounted() && request === latestRequest.current) {
        updateData('totalRows', result.data.totalItems);
        setRows(result.data.results);
      }
    } catch (e) {
      ConsoleHelper.log(e);
      // Raderna för förra filtret ska inte stå kvar som om de hörde till det nya.
      if (isMounted() && request === latestRequest.current) {
        updateData('totalRows', 0);
        setRows([]);
      }
    } finally {
      if (isMounted() && request === latestRequest.current) {
        updateData('loading', false);
      }
      setRefetchDataGrid(false);
    }
  }, [isMounted, searchFilter, setRefetchDataGrid]);

  useEffect(() => {
    if (isMounted()) {
      fetchData();
    }
  }, [fetchData, isMounted]);

  useEffect(() => {
    if (isMounted() && refetchDataGrid) {
      fetchData();
    }
  }, [fetchData, isMounted, refetchDataGrid]);

  const onRowClick = (params: GridCellParams<HelpPageListItem>) => {
    onSelectHelpPage(params.row);
  };

  const columns: GridColDef[] = [
    ...(showSortOrderColumn
      ? [
          {
            field: 'sortOrder',
            headerName: t('helpArticles:columns.sortOrder'),
            sortable: true,
            flex: 0.4,
          } as GridColDef,
        ]
      : []),
    {
      field: 'title',
      headerName: t('helpArticles:columns.title'),
      sortable: true,
      flex: 2,
      renderCell: (params: GridCellParams<HelpPageListItem>) => (
        <Typography sx={{ fontWeight: 500 }} variant='body2'>
          {params.row.title}
        </Typography>
      ),
    },
    ...(hideCategoryColumn
      ? []
      : [
          {
            field: 'categoryId',
            headerName: t('helpArticles:columns.category'),
            sortable: false,
            flex: 1.3,
            renderCell: (params: GridCellParams<HelpPageListItem>) => categoryName(params.row.categoryId),
          } as GridColDef,
        ]),
    {
      field: 'moduleIds',
      headerName: t('helpArticles:columns.modules'),
      sortable: false,
      flex: 1.5,
      renderCell: (params: GridCellParams<HelpPageListItem>) => moduleNames(params.row.moduleIds),
    },
    {
      field: 'requiredAuthority',
      headerName: t('helpArticles:columns.authority'),
      sortable: true,
      flex: 1,
      renderCell: (params: GridCellParams<HelpPageListItem>) => getAuthorityLabel(params.row.requiredAuthority, t),
    },
    {
      field: 'status',
      headerName: t('helpArticles:columns.status'),
      sortable: true,
      flex: 0.9,
      renderCell: (params: GridCellParams<HelpPageListItem>) => <HelpPageStatusChip status={params.row.status} />,
    },
    {
      field: 'updatedAt',
      headerName: t('helpArticles:columns.updatedAt'),
      sortable: true,
      flex: 1,
      renderCell: (params: GridCellParams<HelpPageListItem>) => DateHelper.getDate(params.row.updatedAt ?? params.row.createdAt),
    },
  ];

  return (
    <DataGrid<HelpPageListItem>
      autoHeight
      columns={columns}
      rows={rows}
      onRowClick={onRowClick}
      dataModel={data}
      checkboxSelection={false}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      sortModel={sortModel}
      onSortModelChange={(newSortModel) => {
        const sort = sortModel[0].sort === 'asc' ? 'desc' : 'asc';
        if (newSortModel.length) {
          setSortModel(newSortModel);
        } else {
          setSortModel([{ field: sortModel[0].field, sort }]);
        }
        searchFilterCallback({
          ...searchFilter,
          pageIndex: 0,
          sortCol: newSortModel.length ? newSortModel[0].field : sortModel[0].field,
          sortDir: newSortModel.length ? newSortModel[0].sort : sort,
        });
      }}
      selectionModel={[]}
      onSelectionModelChange={() => ConsoleHelper.log()}
    />
  );
};
